import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import SvgIcon from '@mui/material/SvgIcon';
import ChevronDownIcon from '@untitled-ui/icons-react/build/esm/ChevronDown';
import ChevronRightIcon from '@untitled-ui/icons-react/build/esm/ChevronRight';
import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { RouterLink } from 'src/components/router-link';
import {
  resetNavigationPath,
  setNavigationPathAction,
} from 'src/redux/actions/documentationAction';
import { useMediaQuery } from '@mui/material';

interface SideNavItemProps {
  active?: boolean;
  children?: ReactNode;
  depth?: number;
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  label?: ReactNode;
  open?: boolean;
  path?: string;
  title: string;
  isSideNav: boolean;
  setIsSideNav: any;
}

export const SideNavItem: FC<SideNavItemProps> = (props) => {
  const {
    active,
    children,
    depth = 0,
    disabled,
    external,
    icon,
    label,
    open: openProp,
    path,
    title,
    isSideNav,
    setIsSideNav,
  } = props;
  const [open, setOpen] = useState<boolean>(!!openProp);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const lgdown = useMediaQuery((theme: any) => theme.breakpoints.down('1600'));

  const handleToggle = useCallback((): void => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const checkIsReform = useSelector((state: any) => state.checkIsReformReducer?.isReform);

  const handleItemClick = () => {
    // if (location.pathname?.split('/').pop() === 'create-document') {
    if (
      ['create-document', 'document-edit', 'edit-document'].includes(
        location.pathname?.split('/').pop() || ''
      ) &&
      !checkIsReform
    ) {
      dispatch(setNavigationPathAction(path));
    } else {
      dispatch(resetNavigationPath());
      if (external) {
        window.open(path, '_blank');
      } else if (path) {
        navigate(path);
      }
    }
  };

  const handleSideBar = () => {
    setIsSideNav(true);
  };

  const handleClick = useCallback(() => {
    handleItemClick();
    if (!isSideNav) {
      handleSideBar();
      setOpen(true);
    } else {
      setOpen((prevOpen) => !prevOpen);
    }
  }, [isSideNav, handleToggle, handleSideBar]);

  useEffect(() => {
    if (!isSideNav) {
      setOpen(false);
    }
  }, [isSideNav]);

  let startIcon: ReactNode;

  if (depth === 0) {
    startIcon = icon;
  } else {
    startIcon = (
      <Box
        sx={{
          alignItems: 'center',
          display: 'center',
          height: 20,
          justifyContent: 'center',
          width: 20,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'var(--nav-item-icon-color)',
            borderRadius: '50%',
            height: 4,
            opacity: 0, // remove this if you want it to be visible
            width: 4,
            ...(active && {
              backgroundColor: 'var(--nav-item-icon-active-color)',
              height: 6,
              opacity: 1,
              width: 6,
            }),
          }}
        />
      </Box>
    );
  }

  const offset = depth === 0 ? 0 : (depth - 1) * 16;

  // Branch

  if (children) {
    return (
      <li>
        <ButtonBase
          disabled={disabled}
          onClick={handleClick}
          sx={{
            alignItems: 'center',
            borderRadius: 1,
            display: 'flex',
            justifyContent: isSideNav ? 'flex-start' : 'center',
            pl: isSideNav ? `${16 + offset}px` : '0px',
            pr: isSideNav ? '16px' : '0px',
            transition: 'all 0.5s ease ',
            py: '8px',
            textAlign: 'left',
            width: '100%',
            ...(active && {
              ...(depth === 0 && {
                backgroundColor: 'var(--nav-item-active-bg)',
              }),
            }),
            '&:hover': {
              backgroundColor: 'var(--nav-item-hover-bg)',
            },
          }}
        >
          {startIcon && (
            <Box
              component="span"
              sx={{
                alignItems: 'center',
                color: 'var(--nav-item-icon-color)',
                display: 'inline-flex',
                justifyContent: 'center',
                mr: isSideNav ? 2 : 0,
                ...(active && {
                  color: 'var(--nav-item-icon-active-color)',
                }),
              }}
            >
              {startIcon}
            </Box>
          )}
          {isSideNav && (
            <Box
              component="span"
              sx={{
                color: 'var(--nav-item-color)',
                flexGrow: 1,
                fontFamily: (theme) => theme.typography.fontFamily,
                fontSize: depth > 0 ? 13 : 14,
                fontWeight: depth > 0 ? 500 : 600,
                lineHeight: '26px',
                whiteSpace: 'nowrap',
                ...(active && {
                  color: 'var(--nav-item-active-color)',
                }),
                ...(disabled && {
                  color: 'var(--nav-item-disabled-color)',
                }),
              }}
            >
              {title}
            </Box>
          )}
          {isSideNav && (
            <SvgIcon
              sx={{
                color: 'var(--nav-item-chevron-color)',
                fontSize: 16,
                ml: 2,
              }}
            >
              {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </SvgIcon>
          )}
        </ButtonBase>
        {isSideNav && (
          <Collapse
            in={open}
            sx={{ mt: 0.5 }}
          >
            {children}
          </Collapse>
        )}
      </li>
    );
  }

  // Leaf

  const linkProps = path
    ? external
      ? {
          component: 'a',
          href: path,
          target: '_blank',
        }
      : {
          component: RouterLink,
          href: path,
        }
    : {};

  return (
    <li>
      <ButtonBase
        disabled={disabled}
        onClick={() => {
          handleItemClick();
          if (lgdown && isSideNav) {
            setIsSideNav((prev: any) => !prev);
          }
          // handleItemClick();
          // lgdown && isSideNav && setIsSideNav((prev: any) => !prev);
        }}
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          display: 'flex',
          justifyContent: isSideNav ? 'flex-start' : 'center',
          pl: isSideNav ? `${16 + offset}px` : '0',
          pr: isSideNav ? '16px' : '0',
          py: '8px',
          transition: 'all 0.5s ease ',
          textAlign: 'left',
          width: '100%',
          ...(active && {
            ...(depth === 0 && {
              backgroundColor: 'var(--nav-item-active-bg)',
            }),
          }),
          '&:hover': {
            backgroundColor: 'var(--nav-item-hover-bg)',
          },
        }}
        // {...linkProps}
      >
        {startIcon && (
          <Box
            component="span"
            sx={{
              alignItems: 'center',
              color: 'var(--nav-item-icon-color)',
              display: 'inline-flex',
              justifyContent: 'center',
              mr: isSideNav ? 2 : '0',
              transition: 'all 0.5s ease ',
              ...(active && {
                color: 'var(--nav-item-icon-active-color)',
              }),
            }}
          >
            {startIcon}
          </Box>
        )}
        {isSideNav && (
          <Box
            component="span"
            sx={{
              color: 'var(--nav-item-color)',
              flexGrow: 1,
              fontFamily: (theme) => theme.typography.fontFamily,
              fontSize: depth > 0 ? 13 : 14,
              fontWeight: depth > 0 ? 500 : 600,
              lineHeight: '26px',
              whiteSpace: 'nowrap',
              ...(active && {
                color: 'var(--nav-item-active-color)',
              }),
              ...(disabled && {
                color: 'var(--nav-item-disabled-color)',
              }),
            }}
          >
            {title}
          </Box>
        )}
        {label && isSideNav && (
          <Box
            component="span"
            sx={{ ml: 2 }}
          >
            {label}
          </Box>
        )}
      </ButtonBase>
    </li>
  );
};
